import { useCallback, useMemo, useState } from "react";
import { Source, Layer } from "react-map-gl/maplibre";
import type { CircleLayerSpecification, FillLayerSpecification, LineLayerSpecification } from "maplibre-gl";
import type { Feature, FeatureCollection, Point, Polygon, Position } from "geojson";
import { SELECTED_COLOR } from "../../SubmitJobs/Methods/Bulk/wizard/shared/constants";

export interface ToolOverlay {
	addMarker(coords: Position): void;
	highlightFeature(feature: Feature<Polygon>): void;
	clearOverlay(): void;
	markers: Feature<Point>[];
	highlighted: Feature<Polygon>[];
}

const markerLayer: CircleLayerSpecification = {
	id: "tool-overlay-markers",
	type: "circle",
	source: "tool-overlay-markers",
	paint: {
		"circle-radius": 6,
		"circle-color": SELECTED_COLOR,
		"circle-stroke-width": 2,
		"circle-stroke-color": "#ffffff",
	},
};

const highlightFillLayer: FillLayerSpecification = {
	id: "tool-overlay-highlight-fill",
	type: "fill",
	source: "tool-overlay-highlight",
	filter: ["==", ["get", "isHole"], true],
	paint: {
		"fill-color": "#ff69b4",
		"fill-opacity": 0.2,
	},
};

const highlightLineLayer: LineLayerSpecification = {
	id: "tool-overlay-highlight-line",
	type: "line",
	source: "tool-overlay-highlight",
	paint: {
		"line-color": ["case", ["get", "isHole"], "#ff69b4", "#ffaa00"],
		"line-width": 3,
		"line-dasharray": [3, 2],
	},
};

export function useToolOverlay(): ToolOverlay {
	const [markers, setMarkers] = useState<Feature<Point>[]>([]);
	const [highlighted, setHighlighted] = useState<Feature<Polygon>[]>([]);

	const addMarker = useCallback((coords: Position) => {
		setMarkers((prev) => [
			...prev,
			{
				type: "Feature",
				properties: {},
				geometry: { type: "Point", coordinates: coords },
			},
		]);
	}, []);

	const highlightFeature = useCallback((feature: Feature<Polygon>) => {
		setHighlighted((prev) => [...prev, feature]);
	}, []);

	const clearOverlay = useCallback(() => {
		setMarkers([]);
		setHighlighted([]);
	}, []);

	return useMemo(
		() => ({ addMarker, highlightFeature, clearOverlay, markers, highlighted }),
		[addMarker, highlightFeature, clearOverlay, markers, highlighted],
	);
}

export function ToolOverlayLayer({ overlay }: { overlay: ToolOverlay }) {
	const markerFC: FeatureCollection = {
		type: "FeatureCollection",
		features: overlay.markers,
	};

	const highlightFC: FeatureCollection = {
		type: "FeatureCollection",
		features: overlay.highlighted,
	};

	return (
		<>
			{overlay.markers.length > 0 && (
				<Source id="tool-overlay-markers" type="geojson" data={markerFC}>
					<Layer {...markerLayer} />
				</Source>
			)}
			{overlay.highlighted.length > 0 && (
				<Source id="tool-overlay-highlight" type="geojson" data={highlightFC}>
					<Layer {...highlightFillLayer} />
					<Layer {...highlightLineLayer} />
				</Source>
			)}
		</>
	);
}
